import type {
  ProjectAnnotation,
  ProjectAnnotationMetadata,
  ProjectAnnotationTargetTrackType,
  ProjectAnnotationTargetType,
} from '@/types/project-annotations';
import type { ContentType, StoryboardItem } from '@/types/preview';
import { cn } from '@/lib/utils';

export function getPreviewAnnotationIconClassName(
  actionableCount: number,
  className?: string
): string {
  return cn(className, actionableCount > 0 && 'text-error');
}

export interface PreviewAnnotationTarget {
  targetType: ProjectAnnotationTargetType;
  targetId: string;
  targetStoryboardId: string | null;
  targetResourceType: string | null;
  targetLabel: string;
  targetMetadata: ProjectAnnotationMetadata;
}

export function getPreviewAnnotationTargetKey(
  target: PreviewAnnotationTarget | null | undefined
): string | null {
  if (!target?.targetType || !target.targetId) return null;
  return `${target.targetType}:${target.targetId}`;
}

export function projectAnnotationToPreviewTarget(
  annotation: ProjectAnnotation
): PreviewAnnotationTarget | null {
  if (annotation.targetType === 'none' || !annotation.targetId) {
    return null;
  }
  const annotationTarget = annotation.metadata.annotationTarget;
  const targetType = annotationTarget?.kind ?? annotation.targetType;
  const targetLabel =
    annotationTarget?.label ??
    annotation.targetSummary ??
    annotation.metadata.targetSummary ??
    '批注目标';

  return {
    targetType,
    targetId: annotation.targetId,
    targetStoryboardId: annotation.targetStoryboardId ?? annotationTarget?.storyboardId ?? null,
    targetResourceType: annotation.targetResourceType ?? annotationTarget?.resourceType ?? null,
    targetLabel,
    targetMetadata: {
      ...annotation.metadata,
      targetSummary: annotation.metadata.targetSummary ?? targetLabel,
      annotationTarget: {
        ...(annotationTarget ?? {}),
        kind: targetType,
        label: targetLabel,
      },
    },
  };
}

type ResourceAnnotationType = Extract<ContentType, 'character' | 'location' | 'prop' | 'footage'>;

function compactLabelParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join('-');
}

function readLabel(value: string | null | undefined, fallback: string): string {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function buildMetadata(
  kind: ProjectAnnotationTargetType,
  label: string,
  patch: Omit<NonNullable<ProjectAnnotationMetadata['annotationTarget']>, 'kind' | 'label'> = {}
): ProjectAnnotationMetadata {
  return {
    targetSummary: label,
    annotationTarget: {
      kind,
      label,
      ...patch,
    },
  };
}

function buildStoryboardTrackTarget(params: {
  targetType: ProjectAnnotationTargetType;
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
  labelPart: string;
  trackType: ProjectAnnotationTargetTrackType;
  previewItemId?: string | null;
  planId?: string | null;
}): PreviewAnnotationTarget | null {
  const storyboardId = String(params.storyboardId ?? '').trim();
  if (!storyboardId) return null;

  const storyboardLabel = readLabel(params.storyboardTitle, '当前分镜');
  const label = compactLabelParts([storyboardLabel, params.labelPart]);
  return {
    targetType: params.targetType,
    targetId: params.planId ?? storyboardId,
    targetStoryboardId: storyboardId,
    targetResourceType: params.trackType === 'video' ? 'video' : null,
    targetLabel: label,
    targetMetadata: buildMetadata(params.targetType, label, {
      storyboardId,
      trackType: params.trackType,
      planId: params.planId ?? null,
      previewItemId: params.previewItemId ?? null,
      entityId: null,
      boardSelectionId: null,
      source: 'storyboard_track',
    }),
  };
}

export function resolvePreviewMediaAnnotationTarget(params: {
  kind: Extract<ProjectAnnotationTargetType, 'storyboard_image' | 'storyboard_video'>;
  item: StoryboardItem | null | undefined;
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  const storyboardId = String(params.storyboardId ?? '').trim();
  const item = params.item;
  if (!storyboardId || !item) return null;

  const storyboardLabel = readLabel(params.storyboardTitle, '当前分镜');
  const itemTitle = readLabel(item.title, params.kind === 'storyboard_image' ? '方案' : '视频');

  if (params.kind === 'storyboard_image') {
    const planId = String(item.tagId ?? item.id ?? '').trim();
    if (!planId) return null;
    const label = compactLabelParts([storyboardLabel, '分镜图片', itemTitle]);
    return {
      targetType: 'storyboard_image',
      targetId: planId,
      targetStoryboardId: storyboardId,
      targetResourceType: null,
      targetLabel: label,
      targetMetadata: buildMetadata('storyboard_image', label, {
        storyboardId,
        planId,
        tagId: planId,
        previewItemId: item.id,
      }),
    };
  }

  const videoId = String(item.id ?? '').trim();
  if (!videoId) return null;
  const planId = String(item.tagId ?? '').trim() || null;
  const label = compactLabelParts([storyboardLabel, '视频', itemTitle]);
  return {
    targetType: 'storyboard_video',
    targetId: videoId,
    targetStoryboardId: storyboardId,
    targetResourceType: 'video',
    targetLabel: label,
    targetMetadata: buildMetadata('storyboard_video', label, {
      storyboardId,
      trackType: 'video',
      planId,
      previewItemId: videoId,
      entityId: videoId,
      source: 'storyboard_track',
    }),
  };
}

export function resolveResourceAnnotationTarget(params: {
  resourceId: string | null | undefined;
  resourceType: ResourceAnnotationType | null | undefined;
  resourceName?: string | null;
  item: StoryboardItem | null | undefined;
}): PreviewAnnotationTarget | null {
  const resourceId = String(params.resourceId ?? '').trim();
  const resourceType = params.resourceType ?? null;
  const item = params.item;
  if (!resourceId || !resourceType) return null;

  const tagId = String(item?.tagId ?? '').trim();
  if (tagId) {
    const resourceName = readLabel(params.resourceName, '资源');
    const tagLabel = readLabel(item?.tagLabel ?? item?.title, '标签');
    const label = compactLabelParts([resourceName, tagLabel]);
    return {
      targetType: 'resource_tag',
      targetId: tagId,
      targetStoryboardId: null,
      targetResourceType: resourceType,
      targetLabel: label,
      targetMetadata: buildMetadata('resource_tag', label, {
        storyboardId: null,
        resourceId,
        resourceType,
        resourceName,
        tagId,
        tagLabel,
        previewItemId: item?.id ?? null,
        source: 'resource_library',
      }),
    };
  }

  const label = readLabel(params.resourceName ?? item?.title, '资源');
  return {
    targetType: 'project_resource',
    targetId: resourceId,
    targetStoryboardId: null,
    targetResourceType: resourceType,
    targetLabel: label,
    targetMetadata: buildMetadata('project_resource', label, {
      storyboardId: null,
      resourceId,
      resourceType,
      resourceName: label,
      source: 'resource_library',
    }),
  };
}

export function resolveStoryboardAnnotationTarget(params: {
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  const storyboardId = String(params.storyboardId ?? '').trim();
  if (!storyboardId) return null;

  const label = readLabel(params.storyboardTitle, '当前分镜');
  return {
    targetType: 'storyboard',
    targetId: storyboardId,
    targetStoryboardId: storyboardId,
    targetResourceType: null,
    targetLabel: label,
    targetMetadata: buildMetadata('storyboard', label, {
      storyboardId,
    }),
  };
}

export function resolveStoryboardVideoTrackAnnotationTarget(params: {
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  return buildStoryboardTrackTarget({
    targetType: 'storyboard_video',
    storyboardId: params.storyboardId,
    storyboardTitle: params.storyboardTitle,
    labelPart: '视频',
    trackType: 'video',
  });
}

export function resolveStoryboardResourceTrackAnnotationTarget(params: {
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  return buildStoryboardTrackTarget({
    targetType: 'storyboard_item',
    storyboardId: params.storyboardId,
    storyboardTitle: params.storyboardTitle,
    labelPart: '资源关联',
    trackType: 'resource',
  });
}

export function resolveDialogueAnnotationTarget(params: {
  kind: Extract<ProjectAnnotationTargetType, 'dialogue' | 'voiceover'>;
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  return buildStoryboardTrackTarget({
    targetType: params.kind,
    storyboardId: params.storyboardId,
    storyboardTitle: params.storyboardTitle,
    labelPart: params.kind === 'dialogue' ? '对白' : '配音',
    trackType: params.kind === 'dialogue' ? 'dialogue' : 'voice',
  });
}

export function resolveSoundEffectAnnotationTarget(params: {
  storyboardId: string | null | undefined;
  storyboardTitle?: string | null;
}): PreviewAnnotationTarget | null {
  return buildStoryboardTrackTarget({
    targetType: 'sound_effect',
    storyboardId: params.storyboardId,
    storyboardTitle: params.storyboardTitle,
    labelPart: '音效',
    trackType: 'sound',
  });
}
